'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useToast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/organization-context';
import { apiClient } from '@/lib/api-client';
import {
  DeliveryDto,
  PetrohqApiResponseDeliveryResponse,
} from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useEffect, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

// API에서 가져온 DeliveryDto 타입을 그대로 사용

export function Delivery() {
  const { toast } = useToast();
  const { getAllStores } = useOrganization();

  // 온디맨드 방식: API 호출 시점에만 필터 상태 읽기

  // 상태 관리
  const [deliveryData, setDeliveryData] = useState<DeliveryDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 배송 데이터 가져오기 함수 (온디맨드 방식)
  const fetchDeliveryData = useCallback(async () => {
    // API 호출 시점에 현재 필터 상태 읽기
    const filters = useFilterStore.getState();

    if (!filters.dateRange?.start || !filters.dateRange?.end) {
      return;
    }

    setIsLoading(true);
    try {
      const storeCodeList =
        filters.store.length > 0 ? filters.store : getAllStores();

      // 중앙집중식 파라미터 구축 사용
      const params = filters.buildApiParams('delivery', storeCodeList);

      const response = await apiClient.get('sales/delivery', {
        searchParams: params,
      });
      const data =
        (await response.json()) as PetrohqApiResponseDeliveryResponse;

      if (data.result?.deliveries) {
        setDeliveryData(data.result.deliveries);
      }
    } catch (error) {
      console.error('Delivery data fetch error:', error);
      toast({
        title: '조회 실패',
        description: '배송현황 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [getAllStores, toast]);

  // 초기 데이터 로드 (필터 변경 시 자동 리로드 제거)
  useEffect(() => {
    fetchDeliveryData();
  }, []);

  return (
    <div className='h-full'>
      <ExpandableCard
        title='배송현황'
        breadcrumb={
          <Breadcrumb items={menuBreadcrumbItems('delivery')} />
        }
        pageHeader={
          <PageHeader onSearch={fetchDeliveryData} isLoading={isLoading} />
        }
        fill
      >
        <div
          className='border rounded-lg flex-1 min-h-0'
        >
          <div className='w-full h-full'>
            <RealGridReact
              gridProps={{
                display: { fitStyle: RealGrid.GridFitStyle.FILL },
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                footer: { visible: false },
              }}
              rows={deliveryData}
            >
              <RGDataField
                fieldName={'closedt'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'closecnt'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'saleNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'cardNo2'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilType'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'saleType'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'qty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'cost'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'amount'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'dealCode'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'dealName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'carNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'bigo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataColumn
                fieldName={'closedt'}
                header={{ text: '영업일' }}
                width={70}
              />
              <RGDataColumn
                fieldName={'closecnt'}
                header={{ text: '차수' }}
                width={30}
              />
              <RGDataColumn
                fieldName={'saleNo'}
                header={{ text: '순번' }}
                width={50}
              />
              <RGDataColumn
                fieldName={'cardNo2'}
                header={{ text: '탱크로리' }}
                width={60}
              />
              <RGDataColumn
                fieldName={'oilName'}
                header={{ text: '유종' }}
                width={60}
              />
              <RGDataColumn
                fieldName={'saleType'}
                header={{ text: '판매구분' }}
                width={60}
              />
              <RGDataColumn
                fieldName={'qty'}
                header={{ text: '수량' }}
                width={60}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'cost'}
                header={{ text: '단가' }}
                width={60}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'amount'}
                header={{ text: '금액' }}
                width={60}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'dealCode'}
                header={{ text: '코드' }}
                width={40}
              />
              <RGDataColumn
                fieldName={'dealName'}
                header={{ text: '거래처' }}
                width={60}
              />
              <RGDataColumn fieldName={'carNo'} header={{ text: '차량' }} />
              <RGDataColumn fieldName={'bigo'} header={{ text: '비고' }} />
            </RealGridReact>
          </div>
        </div>

        <div className='mt-4 flex items-center justify-between'>
          <div className='flex items-center space-x-4'>
            <span className='text-sm text-muted-foreground'>
              총 {deliveryData.length}건
            </span>
            {isLoading && (
              <span className='text-sm text-blue-600'>로딩 중...</span>
            )}
          </div>
        </div>
      </ExpandableCard>
    </div>
  );
}
